import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./GamePlayer.scss";
import CollapseIcon from "../../icons/CollapseIcon";
import ExpandIcon from "../../icons/ExpandIcon";
import PlayIcon from "../../icons/PlayIcon";
import UploadIcon from "../../icons/UploadIcon";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import CtaButton from "../../Buttons/CtaButton/CtaButton";
import { PROMPT_DOWNLOAD_SCENARIOS } from "../../DownloadAppPrompt/DownloadAppPrompt";
import DownloadAppPromptLoggedOut from "../../DownloadAppPrompt/DownloadAppPromptLoggedOut";
import Skeleton from "../../Skeleton/Skeleton";
import { useFullscreen } from "../../../hooks/useFullScreen";
import { useWindowDimensions } from "../../../hooks/useWindowDimensions";
import { useShareGameLinkAction } from "../../../hooks/useGamesPage";
import { capitalizeFirstLetter } from "../../../utils/helpers";
import useDisclosure from "../../../hooks/useDisclosure";
import { ThemeVariant } from "../../../utils/constants/generic-types";
import ImageFallbackThumbnail from "../../../images/fallback-thumbnail.png";

export interface GameMeta {
  poster?: string;
  genre?: string;
  categoryCount?: number;
  categoryIcon?: string;
  playCount?: string;
}

export interface GamePlayerProps {
  gameLink: string;
  gameId?: string;
  gameName?: string;
  className?: string;
  gameMeta?: GameMeta;
  isLoading?: boolean;
  onDesktopShare?: () => void;
  onExpandChange?: (isExpanded: boolean) => void;
  useFullscreenMode?: boolean;
  autoStart?: boolean;
}

const MOBILE_BREAKPOINT = 786;

const SKELETON_STYLES = {
  shimmer: { background: "#1b0c3dff" },
  bg: { background: "rgba(51, 23, 120, 0.1)" },
};

export function GamePlayerSkeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`game-container ${className}`}>
      <Skeleton
        height="100%"
        width="100%"
        borderRadius={16}
        shimmerStyle={SKELETON_STYLES.shimmer}
        style={SKELETON_STYLES.bg}
        theme={ThemeVariant.DARK}
      >
        <div className="w-100 h-100" />
      </Skeleton>
      <div className="control-bar">
        <Skeleton
          height={20}
          width={120}
          borderRadius={4}
          shimmerStyle={SKELETON_STYLES.shimmer}
          style={SKELETON_STYLES.bg}
          theme={ThemeVariant.DARK}
        />
        <Skeleton
          height={32}
          width={32}
          borderRadius={8}
          shimmerStyle={SKELETON_STYLES.shimmer}
          style={SKELETON_STYLES.bg}
          theme={ThemeVariant.DARK}
        />
      </div>
    </div>
  );
}

function GamePlayer({
  gameLink,
  gameId,
  gameName = "",
  className = "",
  gameMeta,
  isLoading = false,
  onDesktopShare,
  onExpandChange,
  useFullscreenMode = false,
  autoStart = false,
}: GamePlayerProps) {
  const { t } = useTranslation("games");
  const {
    poster: gamePoster,
    genre: gameGenre = "Casual",
    categoryCount = 1,
    categoryIcon,
    playCount = "0",
  } = gameMeta || {};

  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingFullscreenRef = useRef(false);

  const [hasStarted, setHasStarted] = useState(() => autoStart);
  const [isExpanded, setIsExpanded] = useState(false);

  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const {
    isFullscreen,
    toggle: toggleFullscreen,
    enter: enterFullscreen,
    exit: exitFullscreen,
  } = useFullscreen(containerRef);

  const {
    isOpen: isDownloadPromptOpen,
    onOpen: openDownloadPrompt,
    onClose: closeDownloadPrompt,
  } = useDisclosure();

  const { handleShare } = useShareGameLinkAction({
    gameId,
    desktopShareHandler: onDesktopShare,
  });

  useLayoutEffect(() => {
    if (
      pendingFullscreenRef.current &&
      containerRef.current &&
      hasStarted &&
      isMobile &&
      useFullscreenMode
    ) {
      pendingFullscreenRef.current = false;
      enterFullscreen();
    }
  }, [hasStarted, isMobile, useFullscreenMode, enterFullscreen]);

  const handleStartGame = useCallback(async () => {
    if (isMobile) {
      openDownloadPrompt();
      return;
    }
    setHasStarted(true);
    if (useFullscreenMode) {
      await enterFullscreen();
    }
  }, [isMobile, openDownloadPrompt, enterFullscreen, useFullscreenMode]);

  useEffect(() => {
    if (!autoStart) return;
    setHasStarted(true);
  }, [autoStart]);

  const handleContinueOnWeb = useCallback(() => {
    closeDownloadPrompt();
    if (useFullscreenMode) {
      pendingFullscreenRef.current = true;
    }
    setHasStarted(true);
  }, [closeDownloadPrompt, useFullscreenMode]);

  const handleExitGame = useCallback(async () => {
    if (isFullscreen) {
      await exitFullscreen();
    }
    setHasStarted(false);
  }, [isFullscreen, exitFullscreen]);

  const handleToggleExpand = useCallback(async () => {
    if (useFullscreenMode) {
      await toggleFullscreen();
    } else {
      setIsExpanded((prev) => !prev);
    }
  }, [useFullscreenMode, toggleFullscreen]);

  useEffect(() => {
    const currentState = useFullscreenMode ? isFullscreen : isExpanded;
    onExpandChange?.(currentState);

    requestAnimationFrame(() => {
      iframeRef.current?.focus();
    });
  }, [isExpanded, isFullscreen, useFullscreenMode, onExpandChange]);

  const shouldShowMobileCard = isMobile && !hasStarted;
  const isCurrentlyExpanded = useFullscreenMode ? isFullscreen : isExpanded;

  if (shouldShowMobileCard) {
    return (
      <div className="MobileGameCard">
        <div className="GamePoster">
          <Skeleton
            isLoading={isLoading}
            height="100%"
            width="100%"
            borderRadius={32}
            shimmerStyle={{ background: "#1b0c3dff" }}
            style={{ background: "rgba(51, 23, 120, 0.1)" }}
            theme={ThemeVariant.DARK}
          >
            <Image
              src={gamePoster || ImageFallbackThumbnail}
              alt={gameName}
              className="PosterImage"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = ImageFallbackThumbnail;
              }}
            />
          </Skeleton>
        </div>
        <h1 className="text-capitalize GameTitle">{gameName}</h1>
        <div className="GameMeta">
          <span className="MetaItem">
            <PlayIcon fill="#bfbfbf" /> {playCount}
          </span>
          <span className="MetaDot">•</span>
          <span className="MetaItem">
            {categoryIcon && (
              <Image
                src={categoryIcon}
                alt={gameGenre}
                className="GenreIcon"
                width={16}
                height={16}
              />
            )}
            {capitalizeFirstLetter(gameGenre)}
            {categoryCount > 1 && ` +${categoryCount - 1}`}
          </span>
          <span className="MetaDot">•</span>
          <button
            className="ShareButton"
            aria-label="Share"
            onClick={handleShare}
          >
            <UploadIcon width={12} height={12} />
          </button>
        </div>
        <CtaButton variant="default" size="lg" onClick={handleStartGame} disabled={isLoading}>
          {isLoading ? <CustomSpinner size="sm" /> : t("start_game")}
        </CtaButton>
        <DownloadAppPromptLoggedOut
          isOpen={isDownloadPromptOpen}
          onOpen={openDownloadPrompt}
          onClose={closeDownloadPrompt}
          promptDownloadScenario={PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS}
          secondaryButtonText={t("continue_on_web")}
          onSecondaryAction={handleContinueOnWeb}
        />
      </div>
    );
  }

  if (isMobile && hasStarted) {
    return (
      <div
        ref={containerRef}
        className={`MobileGameFullscreen ${
          isCurrentlyExpanded ? "fullscreen-mode" : ""
        }`}
      >
        <div className="MobileGameHeader">
          <p className="MobileGameTitle">{gameName}</p>
          <button className="MobileGameExit" onClick={handleExitGame}>
            {t("exit")}
          </button>
        </div>
        <iframe
          src={gameLink}
          title={gameName}
          width="100%"
          height="100%"
          className="game-frame"
          allow="fullscreen"
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`game-container ${isCurrentlyExpanded ? "expanded" : ""} ${
        useFullscreenMode ? "fullscreen-mode" : ""
      } ${className}`}
    >
      {isLoading ? (
        <Skeleton
          isLoading={isLoading}
          height="100%"
          width="100%"
          borderRadius={16}
          shimmerStyle={{ background: "#1b0c3dff" }}
          style={{ background: "rgba(51, 23, 120, 0.1)" }}
          theme={ThemeVariant.DARK}
        >
          <div className="w-100 h-100" />
        </Skeleton>
      ) : (
        <iframe
          ref={iframeRef}
          src={gameLink}
          title={gameName}
          width="100%"
          height="100%"
          className="game-frame"
          allow="fullscreen"
        />
      )}
      <div className="control-bar">
        <p className="text-capitalize game-name Grotesk">{gameName}</p>
        <button
          className="control-button"
          aria-label={
            isCurrentlyExpanded ? t("exit_full_view") : t("enter_full_view")
          }
          onClick={handleToggleExpand}
        >
          {isCurrentlyExpanded ? <CollapseIcon /> : <ExpandIcon />}
        </button>
      </div>
    </div>
  );
}

export default GamePlayer;
